import { Status, Statuses } from './status';
import { ValidationError } from './validation.error';

export interface ScheduledJobStep {
  name: string;
  run: () => Promise<void>;
}

export interface ScheduledJob {
  name: string;
  cronExpression: string;
  status: Status;
  startDate: Date;
  endDate?: Date;
  lastStepRan?: string;
  lastRunStartDate?: Date;
  lastRunEndDate?: Date;
  lastSuccessStartDate?: Date;
  error?: string;
  steps?: ScheduledJobStep[];
  addStep(run: () => Promise<void>, name?: string): ScheduledJob;
  toJSON(): Partial<ScheduledJob>;
  isDisabled(): boolean;
}

export class ScheduledJobModel implements ScheduledJob {
  name: string;
  cronExpression: string;
  status = Statuses.NOT_STARTED;
  startDate: Date;
  endDate?: Date;
  lastStepRan?: string;
  lastRunStartDate?: Date;
  lastRunEndDate?: Date;
  lastSuccessStartDate?: Date;
  error?: string;
  steps?: ScheduledJobStep[];

  static create(props: Partial<ScheduledJob>): ScheduledJob {
    if (!props || !props.name || !props.cronExpression) {
      throw new ValidationError('ScheduledJob is missing name or cron expression');
    }

    return new ScheduledJobModel(props);
  }

  addStep(this: ScheduledJob, run: () => Promise<void>, name?: string): ScheduledJob {
    const steps = this.steps || [];

    return ScheduledJobModel.create({
      ...this,
      steps: steps.concat({ run, name: `Step ${steps.length + 1}` })
    });
  }

  isDisabled(): boolean {
    const now = new Date();

    return this.startDate > now || (this.endDate != null && this.endDate < now);
  }

  toJSON(): Partial<ScheduledJob> {
    return {
      name: this.name,
      cronExpression: this.cronExpression,
      status: this.status,
      startDate: this.startDate,
      endDate: this.endDate,
      lastStepRan: this.lastStepRan,
      lastRunStartDate: this.lastRunStartDate,
      lastRunEndDate: this.lastRunEndDate,
      lastSuccessStartDate: this.lastSuccessStartDate,
      error: this.error
    };
  }

  private constructor(props: Partial<ScheduledJob>) {
    this.name = props.name || '';
    this.cronExpression = props.cronExpression || '';
    this.status = props.status || Statuses.NOT_STARTED;
    this.startDate = props.startDate ? new Date(props.startDate) : new Date();

    if (props.endDate) {
      this.endDate = new Date(props.endDate);
    }

    this.lastStepRan = props.lastStepRan;

    if (props.lastRunStartDate) {
      this.lastRunStartDate = new Date(props.lastRunStartDate);
    }

    if (props.lastRunEndDate) {
      this.lastRunEndDate = new Date(props.lastRunEndDate);
    }

    if (props.lastSuccessStartDate) {
      this.lastSuccessStartDate = new Date(props.lastSuccessStartDate);
    }

    if (props.error) {
      this.error = props.error;
    }

    if (props.steps) {
      this.steps = props.steps.map(step => ({ ...step }));
    }
  }
}



// WEBPACK FOOTER //
// ./src/gs-core/common/models/scheduled-job.ts